import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './Process.module.css';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: '01',
    title: 'היכרות ותכנון צרכים',
    description:
      'אנחנו מתחילים בפגישות היכרות שמטרתן להבין אתכם לעומק - מי אתם, מה הסגנון האישי או העסקי שלכם, מה הצרכים הפרקטיים, התקציב ולוחות הזמנים. בשלב זה אני בונה פרוגרמה מדויקת - מסמך שמפרט את כל הפונקציות הנדרשות בחלל.',
  },
  {
    number: '02',
    title: 'גיבוש קונספט ותכנון מעשי',
    description:
      'אני מפתחת קונספט עיצובי שמשקף את הערכים, האופי והחזון של הפרויקט, ויוצרת ממנו תכניות מדויקות - תכנון פונקציונלי, חלוקות חלל, סקיצות, הדמיות ובחירת חומרים. הכול נעשה מתוך ראייה רחבה שמשלבת עיצוב, פונקציונליות ותקציב.',
  },
  {
    number: '03',
    title: 'ליווי, ביצוע וניהול פרויקט',
    description:
      'אני מלווה אתכם שלב אחרי שלב - תיאום מול קבלנים, נגר, חשמל, מיזוג ושאר אנשי מקצוע, פתרון בעיות בזמן אמת והקפדה על כל פרט. עד לרגע שבו החלל מוכן ומושלם.',
  },
];

const ProcessSection = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const rowRefs = useRef([]);
  const numberFillRefs = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      // Header staggered entrance
      if (headerRef.current) {
        const els = headerRef.current.children;
        gsap.fromTo(
          els,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1.2,
            stagger: 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 70%',
              toggleActions: 'play none none none',
            },
          }
        );
      }

      // Row animations
      rowRefs.current.filter(Boolean).forEach((row, i) => {
        const isReversed = i % 2 === 1;

        // Number parallax on scroll
        const numberEl = row.querySelector('[data-number]');
        if (numberEl) {
          gsap.to(numberEl, {
            yPercent: -25,
            ease: 'none',
            scrollTrigger: {
              trigger: row,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.5,
            },
          });
        }

        // Gradient number fill reveal via clipPath
        const fillEl = numberFillRefs.current[i];
        if (fillEl) {
          gsap.fromTo(
            fillEl,
            { clipPath: 'inset(100% 0 0 0)' },
            {
              clipPath: 'inset(0% 0 0 0)',
              ease: 'none',
              scrollTrigger: {
                trigger: row,
                start: 'top 82%',
                end: 'top 35%',
                scrub: 1,
              },
            }
          );
        }

        // Card entrance with perspective tilt
        const card = row.querySelector('[data-card]');
        if (card) {
          gsap.fromTo(
            card,
            {
              opacity: 0,
              y: 70,
              x: isReversed ? 50 : -50,
              rotateY: isReversed ? -4 : 4,
            },
            {
              opacity: 1,
              y: 0,
              x: 0,
              rotateY: 0,
              duration: 1.2,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: row,
                start: 'top 82%',
                toggleActions: 'play none none none',
              },
            }
          );
        }

        // Staggered content elements
        const revealEls = row.querySelectorAll('[data-reveal]');
        if (revealEls.length) {
          gsap.fromTo(
            revealEls,
            { opacity: 0, y: 20 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.1,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: row,
                start: 'top 72%',
                toggleActions: 'play none none none',
              },
            }
          );
        }

        // Corner accents pop-in
        const corners = row.querySelectorAll('[data-corner]');
        if (corners.length) {
          gsap.fromTo(
            corners,
            { opacity: 0, scale: 0.4 },
            {
              opacity: 1,
              scale: 1,
              duration: 0.7,
              delay: 0.5,
              stagger: 0.12,
              ease: 'back.out(2.5)',
              scrollTrigger: {
                trigger: row,
                start: 'top 72%',
                toggleActions: 'play none none none',
              },
            }
          );
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className={styles.section}>
      {/* Background decoration */}
      <div className={styles.bgPattern} />
      <div className={styles.bgOrb1} />
      <div className={styles.bgOrb2} />

      <div ref={headerRef} className={styles.header}>
        <h2 className={styles.title}>אז איך עובד התהליך איתי..</h2>
        <p className={styles.introText}>
          בעולם הבנייה והעיצוב, השילוב בין אסתטיקה מדויקת לניהול מקצועי הוא לא מותרות - הוא תנאי להצלחה.
          אין מקום לעיצוב שהוא רק יפה - נדרשת הבנה עמוקה של הצרכים, ניהול תקציבים וספקים, והקפדה על כל פרט.
          ככה בדיוק אני עובדת - בתהליך מסודר שמלווה אתכם מהרגע הראשון ועד לרגע שהחלל מוכן.
        </p>
      </div>

      <div className={styles.stepsContainer}>
        {/* Decorative flowing path */}
        <svg className={styles.flowPath} viewBox="0 0 100 100" preserveAspectRatio="none">
          <path
            d="M 78 0 C 78 10, 74 14, 50 17 C 26 20, 22 27, 22 34 C 22 41, 26 46, 50 50 C 74 54, 78 59, 78 66 C 78 73, 74 80, 50 83 C 26 86, 22 92, 22 100"
            fill="none"
            stroke="rgba(68, 79, 71, 0.18)"
            strokeWidth="0.6"
            strokeDasharray="2 3"
          />
          <circle cx="50" cy="17" r="1.5" fill="rgba(197, 212, 184, 0.35)" />
          <circle cx="50" cy="50" r="1.5" fill="rgba(197, 212, 184, 0.35)" />
          <circle cx="50" cy="83" r="1.5" fill="rgba(197, 212, 184, 0.35)" />
        </svg>

        {steps.map((step, i) => (
          <div
            key={i}
            ref={(el) => (rowRefs.current[i] = el)}
            className={`${styles.stepRow} ${i % 2 === 1 ? styles.stepRowReversed : ''}`}
          >
            {/* Number */}
            <div className={styles.numberSide} data-number>
              <div className={styles.numberFrame}>
                <span className={styles.numberOutline}>{step.number}</span>
                <span
                  ref={(el) => (numberFillRefs.current[i] = el)}
                  className={styles.numberFill}
                >
                  {step.number}
                </span>
              </div>
            </div>

            {/* Card */}
            <div className={styles.cardWrap} data-card>
              <div className={styles.card}>
                <span className={`${styles.corner} ${styles.cornerTR}`} data-corner />
                <span className={`${styles.corner} ${styles.cornerBL}`} data-corner />

                <h3 className={styles.stepTitle} data-reveal>{step.title}</h3>
                <p className={styles.stepDesc} data-reveal>{step.description}</p>

                <div className={styles.stepDots} data-reveal>
                  {steps.map((_, j) => (
                    <span
                      key={j}
                      className={`${styles.dot} ${j === i ? styles.dotActive : ''} ${j < i ? styles.dotDone : ''}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Row separators */}
        <div className={styles.separators}>
          {[0, 1].map((i) => (
            <div key={i} className={styles.separator}>
              <span className={styles.sepDot} />
              <span className={styles.sepLine} />
              <span className={styles.sepDot} />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.ctaWrapper}>
        <a href="#contact" className={styles.ctaButton}>
          <span className={styles.ctaText}>לשיחת ייעוץ ללא עלות לחצו כאן</span>
        </a>
      </div>
    </section>
  );
};

export default ProcessSection;